import { useCallback, useMemo, useState } from "react";

export interface ReportOrg {
  id: string;
  name: string;
}

interface SelectionState {
  ids: Set<string>;
  namesById: Map<string, string>;
}

const EMPTY_STATE: SelectionState = { ids: new Set(), namesById: new Map() };

/**
 * Generic organization selection state for "reports from anywhere".
 * Holds ids plus a name map so the report dialog can show a summary
 * without requiring the caller to keep full org objects around.
 */
export const useReportOrgSelection = () => {
  const [state, setState] = useState<SelectionState>(EMPTY_STATE);

  const toggleSelection = useCallback((org: ReportOrg) => {
    if (!org.id) return;
    setState(({ ids, namesById }) => {
      const nextIds = new Set(ids);
      const nextNames = new Map(namesById);
      if (nextIds.has(org.id)) {
        nextIds.delete(org.id);
      } else {
        nextIds.add(org.id);
        nextNames.set(org.id, org.name);
      }
      return { ids: nextIds, namesById: nextNames };
    });
  }, []);

  const selectMultiple = useCallback((orgs: ReportOrg[]) => {
    const valid = orgs.filter((org) => org.id);
    if (valid.length === 0) return;
    setState(({ ids, namesById }) => {
      const nextIds = new Set(ids);
      const nextNames = new Map(namesById);
      valid.forEach((org) => {
        nextIds.add(org.id);
        nextNames.set(org.id, org.name);
      });
      return { ids: nextIds, namesById: nextNames };
    });
  }, []);

  const unselectMultiple = useCallback((orgIds: string[]) => {
    setState(({ ids, namesById }) => {
      const nextIds = new Set(ids);
      const nextNames = new Map(namesById);
      orgIds.forEach((id) => {
        nextIds.delete(id);
        nextNames.delete(id);
      });
      return { ids: nextIds, namesById: nextNames };
    });
  }, []);

  const clearSelection = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  const isSelected = useCallback(
    (orgId: string) => state.ids.has(orgId),
    [state.ids]
  );

  const selectedOrgs = useMemo<ReportOrg[]>(
    () =>
      Array.from(state.ids).map((id) => ({
        id,
        name: state.namesById.get(id) ?? id,
      })),
    [state.ids, state.namesById]
  );

  return {
    selectedIds: state.ids,
    selectedOrgs,
    toggleSelection,
    selectMultiple,
    unselectMultiple,
    clearSelection,
    isSelected,
    selectedCount: state.ids.size,
  };
};
